import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'urql';

import {
  Card,
  CardBody,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  NakedForm,
  SubmitButton,
  SwitchField,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import {
  BlockInstantBankingMutation,
  BlockInstantBankingMutationVariables,
  PlayerIsInstantBankingFormQuery,
  UnblockInstantBankingMutation,
  UnblockInstantBankingMutationVariables,
} from './__generated__/PlayerInstantBankingForm';

const query = graphql`
  query SanityPlayerInstantBankingForm {
    sanityPlayerInstantBankingForm {
      title {
        ...SanityLocaleString
      }
      isInstantBankingBlocked {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const blockInstantBankingQuery = gql`
  mutation BlockInstantBanking($playerId: ID!) {
    blockInstantBanking: blockInstantBankingV2(playerId: $playerId) {
      id
      isInstantBankingBlocked
      instantBankingBlock {
        activeFrom
      }
    }
  }
`;

const unblockInstantBankingQuery = gql`
  mutation UnblockInstantBanking($playerId: ID!) {
    unblockInstantBanking(playerId: $playerId) {
      id
      isInstantBankingBlocked
      instantBankingBlock {
        activeFrom
      }
    }
  }
`;

const playerQuery = gql`
  query PlayerIsInstantBankingForm($playerId: ID!) {
    player(playerId: $playerId) {
      id
      isInstantBankingBlocked
    }
  }
`;

const InstantBankingFields: FC<{
  playerId: string;
  isInstantBankingBlocked: boolean;
}> = ({ playerId, isInstantBankingBlocked }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerInstantBankingFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();

  const form = staticData.sanityPlayerInstantBankingForm;

  const { close } = useModalContext();

  const { t } = useTranslate();

  const [{ fetching: blockFetching }, blockInstantBanking] = useMutation<
    BlockInstantBankingMutation,
    BlockInstantBankingMutationVariables
  >(blockInstantBankingQuery);

  const [{ fetching: unblockFetching }, unblockInstantBanking] = useMutation<
    UnblockInstantBankingMutation,
    UnblockInstantBankingMutationVariables
  >(unblockInstantBankingQuery);

  const defaultValues = {
    isInstantBankingBlocked,
  };

  const methods = useForm<typeof defaultValues>({ defaultValues });
  const nextValue = methods.watch('isInstantBankingBlocked');
  const isUnchanged = nextValue === isInstantBankingBlocked;

  const onSubmit = (values: typeof defaultValues) => {
    const blockOrUnBlock = values.isInstantBankingBlocked
      ? blockInstantBanking
      : unblockInstantBanking;

    blockOrUnBlock({ playerId }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        close();
      }
    });
  };

  if (!form) {
    return null;
  }

  return (
    <Card
      size="md"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <CardBody>
        <div className="p-3">
          <NakedForm
            methods={methods}
            onSubmit={onSubmit}
            className="grid gap-6"
          >
            <SwitchField
              title={t(form.isInstantBankingBlocked)}
              name="isInstantBankingBlocked"
              labelPlacement="above"
              className="col-span-full"
              allowUndefinedState={false}
            />
            <ErrorMessage message={errorMessage} />
            <SubmitButton
              value={t(form.submit)}
              disabled={blockFetching || unblockFetching || isUnchanged}
            />
          </NakedForm>
        </div>
      </CardBody>
    </Card>
  );
};

const PlayerInstantBankingForm: FC<{ playerId: string }> = ({ playerId }) => {
  const [{ data }] = useQuery<PlayerIsInstantBankingFormQuery>({
    query: playerQuery,
    variables: { playerId },
  });

  if (!data?.player) {
    return null;
  }

  return (
    <InstantBankingFields
      playerId={data.player.id}
      isInstantBankingBlocked={!!data.player.isInstantBankingBlocked}
    />
  );
};

export default PlayerInstantBankingForm;
